import { LitEntity, SirenActionMixin, html, css, renderIf } from '../utility/lit-entity';
import { sharedStyles } from '../styles/shared-styles';
import '../common/bn-icon';
import '../common/bn-loading';

class TagListItem extends SirenActionMixin(LitEntity) {
  static get properties() {
    return {
      editing: { type: Boolean, attribute: false },
      inputValue: { type: String, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        border-bottom: 1px solid;
        display: table-row;
        vertical-align: inherit;
        border-color: inherit;
      }
      .actions {
        display: flex;
      }
      .actions > * {
        margin: 0 2px;
        white-space: nowrap;
      }
      .actions > *:first-child {
        margin-left: 0;
      }
      .actions > *:last-child {
        margin-right: 0;
      }
      td {
        word-break: break-word;
      }
    `];
  }

  render() {
    const { entity } = this;
    if (!this.entity) {
      return html`<bn-loading></bn-loading>`;
    }

    let valueDisplay;
    if (!this.editing) {
      valueDisplay = entity ? entity.properties.value : '';
    } else {
      valueDisplay = html`
        <input
          type="text"
          name="tag-value"
          id="tag-value"
          class="value-input"
          value="${entity ? entity.properties.value : ''}"
          @input="${this._onInputChanged}"
        >
      `;
    }

    return html`
      <td class="key">${entity.properties.key}</td>
      <td class="value">${valueDisplay}</td>
      ${renderIf(this.entity.actions.length > 0, () => html`
        <td>
          <div class="actions">
            ${this._renderActions()}
          </div>
        </td>
      `)}
    `;
  }

  _toggleEditing() {
    this.editing = !this.editing;
    this.inputValue = this.entity ? this.entity.properties.value : '';
  }

  _onInputChanged(e) {
    this.inputValue = e.target.value;
  }

  _renderActions() {
    if (this.editing) {
      return html`
        <button class="button" @click="${this._toggleEditing}">
          <bn-icon name="fal fa-times"></bn-icon>
        </button>

        <button
          class="button button--outlined"
          @click="${this._onClickSave}"
          ?disabled="${this.inputValue === ''}"
        >
          Save
        </button>
      `;
    }

    return html`
      ${renderIf(this.entity.hasActionByName('update-tag'), html`
        <button class="button button--outlined" @click="${this._toggleEditing}">
          Edit
        </button>
      `)}

      ${renderIf(this.entity.hasActionByName('delete-tag'), html`
        <bn-action
          actionname="delete-tag"
          buttonstyle="outlinedError"
          ?destructive="${true}"
          ?suppressflash="${true}"
          .preSubmit="${() => this._preDestroy('delete')}"
          .href="${this.href}"
        >
          <span slot="header"></span>
          <span slot="submit-content">
            <bn-icon name="fal fa-trash-alt"></bn-icon>
          </span>
        </bn-action>
      `)}

      ${renderIf(this.entity.hasActionByName('clear-tag'), html`
        <bn-action
          actionname="clear-tag"
          submittext="Clear"
          ?destructive="${true}"
          ?suppressflash="${true}"
          .preSubmit="${() => this._preDestroy('clear')}"
          .href="${this.href}"
        >
          <span slot="header"></span>
        </bn-action>
      `)}
    `;
  }

  _error(message) {
    this.dispatchEvent(new ErrorEvent('error', { message, bubbles: true, composed: true }));
  }

  async _onClickSave() {
    const action = this.entity.getActionByName('update-tag');
    if (!action) {
      this._error('Could not find [update-tag] action');
      return;
    }
    const fields = this.getActionFields(action);
    fields.set('key', this.entity.properties.key);
    fields.set('value', this.inputValue);

    try {
      const { entity, error } = await this.submitAction(action, fields);
      if (error) {
        this._error(error.message);
      } else {
        this.dispatchEvent(new CustomEvent('save', { detail: { entity }, bubbles: true, composed: true }));
        this.editing = false;
      }
    } catch (err) {
      this._error('Could not update tag.');
    }
  }

  _preDestroy(destroyTerm) {
    const confirmMessage = `Are you sure you want to ${destroyTerm} this tag? This action cannot be undone.`;
    return window.confirm(confirmMessage);
  }
}

window.customElements.define('tag-list-item', TagListItem);
